import i18next, { t } from 'i18next';
import { Client } from '../../../DndComponents/ContractsDnd/types';

export const translate = (familyName: string) => {
  const key = `equipmentFamilyNames.${familyName}`;
  return i18next.exists(key) ? t(key) : familyName;
};

/**
 * Familles à conserver sur la configuration par défaut après modification des
 * familles de son profil parent. Une configuration par défaut sans famille n'a
 * pas de sens : si le profil ne porte plus aucune des familles retenues, on
 * reprend toutes celles qui restent.
 */
export const getSyncedDefaultFamilies = (
  defaultFamilies: number[],
  profileFamilies: number[]
): number[] => {
  const keptFamilies = defaultFamilies.filter((familyId) => profileFamilies.includes(familyId));
  return keptFamilies.length > 0 ? keptFamilies : profileFamilies;
};

/** Préfixe chaque filtre de la recherche des profils pour l'appliquer à une table liée au profil. */
export const formatFilterString = (filterString: string, param: string): string =>
  '&' +
  filterString
    .split('&')
    .filter(Boolean)
    .map((key) => param + key)
    .join('&') +
  '&';

/** Familles présentes dans la nouvelle sélection du profil et absentes de l'ancienne. */
export const getAddedFamilies = (previousFamilies: number[], nextFamilies: number[]): number[] =>
  nextFamilies.filter((familyId) => !previousFamilies.includes(familyId));

export const getCustomers = (customers: number[], clients: Client[]) =>
  customers.map((customerId) => {
    const client = clients.find((c) => c.id === customerId);
    return {
      label: client?.oss_code || String(customerId),
      value: customerId,
      contracts: client?.contracts || []
    };
  }) || [];

export const getContracts = (contracts: number[], clients: Client[]) =>
  contracts
    .map((contractId) => {
      const parentClient = clients.find((c) => c.contracts.some((ct) => ct.id === contractId));
      const contract = parentClient?.contracts.find((ct) => ct.id === contractId);
      return {
        label: contract?.oss_code || String(contractId),
        value: contractId,
        clientName: parentClient?.oss_code || ''
      };
    })
    .filter(Boolean) || [];

export const getAuthorisedData = (
  clients: Client[],
  authorised_customers: number[],
  authorised_contracts: number[],
  excluded_customers: number[],
  excluded_contracts: number[]
): Client[] => {
  // Créer un Set pour les exclusions pour des recherches plus rapides
  const excludedCustomersSet = new Set(excluded_customers);
  const excludedContractsSet = new Set(excluded_contracts);

  // Si authorised_customers ET authorised_contracts sont vides, on prend tous les clients
  const shouldIncludeAllCustomers =
    authorised_customers.length === 0 && authorised_contracts.length === 0;
  const authorisedCustomersSet = new Set(authorised_customers);
  const authorisedContractsSet = new Set(authorised_contracts);

  // Filtrer les clients
  const result = clients
    .filter((client) => {
      // Exclure les clients exclus
      if (excludedCustomersSet.has(client.id)) {
        return false;
      }

      // Si authorised_customers est vide, on garde tous les clients (sauf exclus)
      // Sinon, on vérifie si le client est autorisé OU s'il a au moins un contrat autorisé
      if (!shouldIncludeAllCustomers) {
        const isCustomerAuthorised = authorisedCustomersSet.has(client.id);
        const hasAuthorisedContract = client.contracts.some((contract) =>
          authorisedContractsSet.has(contract.id)
        );

        if (!isCustomerAuthorised && !hasAuthorisedContract) {
          return false;
        }
      }

      return true;
    })
    .map((client) => {
      // Nombre total de contrats du client
      const totalContracts = client.contracts.length;

      // Filtrer les contrats du client
      const filteredContracts = client.contracts.filter((contract) => {
        // Exclure les contrats exclus
        if (excludedContractsSet.has(contract.id)) {
          return false;
        }

        // Si on n'autorise pas tous les clients, vérifier les contrats autorisés
        if (!shouldIncludeAllCustomers && authorised_contracts.length > 0) {
          // Si le client n'est pas dans authorised_customers, on ne garde que les contrats autorisés
          if (!authorisedCustomersSet.has(client.id)) {
            return authorisedContractsSet.has(contract.id);
          }
        }

        return true;
      });

      return {
        ...client,
        contracts: filteredContracts,
        contractsLength: totalContracts
      };
    })
    .filter((client) => client.contracts.length > 0); // Enlever les clients sans contrats
  return result;
};

export const displayClientsOrContracts = (values: number[], clients: Client[]) => {
  const maxDisplayCount = 3;
  const formattedValues =
    values &&
    values.map((id) => {
      const client = clients?.find((c) => c.id === id);
      if (client) return client.oss_code;
      for (const c of clients ?? []) {
        const contract = c.contracts.find((ct) => ct.id === id);
        if (contract) return contract.oss_label || contract.oss_code;
      }
      return id.toString();
    });
  if (formattedValues && formattedValues.length > maxDisplayCount) {
    const displayedformattedValues = formattedValues.slice(0, maxDisplayCount).join(', ');
    const remainingCount = formattedValues.length - maxDisplayCount;
    return `${displayedformattedValues} et ${remainingCount} autre${remainingCount > 1 ? 's' : ''}`;
  }
  return formattedValues && formattedValues.length > 0 ? formattedValues.join(', ') : '-';
};
